import { Badge, Group, Paper, Stack, Tabs, Text, Title } from "@mantine/core";
import {
  IconCalendar,
  IconFile,
  IconInfoCircle,
  IconUsersGroup,
} from "@tabler/icons-react";
import { format, parseISO } from "date-fns";
import { useState } from "react";
import { Redirect, useParams } from "wouter";
import { client } from "@/api-client";
import type {
  GroupParticipantViewOutput,
  GroupWithRequestOutput,
} from "@/api-types";
import { FancyLoader } from "@/components/FancyLoader";
import { GroupJoinRequest } from "@/components/GroupJoinRequest";
import { Header } from "@/components/Header";
import { OwnerGroupFiles } from "@/components/OwnerGroupFiles";
import { Portrait } from "@/components/Portrait";
import { useHashTab } from "@/hooks/useHashTab";
import {
  createPath,
  OwnerRoutes,
  UniversalRoutes,
  useNavigator,
} from "@/routers/routes";

const GroupCard = ({ group }: { group: GroupParticipantViewOutput }) => {
  const { navigator } = useNavigator();

  const getStatusColor = (status: string) => {
    switch (status) {
      case "GRANTED":
        return "green";
      case "REQUESTED":
        return "yellow";
      case "REJECTED":
        return "red";
      default:
        return "gray";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "GRANTED":
        return "Active";
      case "REQUESTED":
        return "Pending";
      case "REJECTED":
        return "Rejected";
      default:
        return status;
    }
  };

  return (
    <Paper
      p="md"
      withBorder
      style={{ cursor: "pointer" }}
      onClick={navigator({
        path: UniversalRoutes.GROUPS.DETAIL,
        params: { groupId: group.id },
      })}
    >
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Title order={4}>{group.name}</Title>
          <Badge color={getStatusColor(group.status)} variant="light">
            {getStatusText(group.status)}
          </Badge>
        </Group>

        {group.description && (
          <Text size="sm" c="dimmed" lineClamp={2}>
            {group.description}
          </Text>
        )}

        <Group justify="space-between" align="center">
          <Group gap="xs">
            <Portrait
              profile={{
                firstName: group.trainer.firstName,
                lastName: group.trainer.lastName,
                portraitFullPath: group.trainer.portraitFullPath,
              }}
              size="sm"
              who="person"
            />
            <Text size="sm" c="dimmed">
              {group.trainer.firstName} {group.trainer.lastName}
            </Text>
          </Group>

          <Group gap="md">
            <Group gap="xs">
              <IconFile size={16} color="gray" />
              <Text size="sm" c="dimmed">
                {group.files.length} files
              </Text>
            </Group>

            <Group gap="xs">
              <IconCalendar size={16} color="gray" />
              <Text size="sm" c="dimmed">
                {group.sessions.length} sessions
              </Text>
            </Group>
          </Group>
        </Group>
      </Stack>
    </Paper>
  );
};

const RequestCard = ({ groupInfo }: { groupInfo: GroupWithRequestOutput }) => {
  const { group, membership } = groupInfo;
  const { navigator } = useNavigator();

  if (!membership) {
    return null;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "REQUESTED":
        return "yellow";
      case "REJECTED":
        return "red";
      default:
        return "gray";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "REQUESTED":
        return "Pending";
      case "REJECTED":
        return "Rejected";
      default:
        return status;
    }
  };

  return (
    <Paper
      p="md"
      withBorder
      style={{ cursor: "pointer" }}
      onClick={navigator({
        path: UniversalRoutes.GROUPS.DETAIL,
        params: { groupId: group.id },
      })}
    >
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Title order={4}>{group.name}</Title>
          <Badge color={getStatusColor(membership.status)} variant="light">
            {getStatusText(membership.status)}
          </Badge>
        </Group>

        <Text size="sm" c="dimmed" lineClamp={2}>
          Trainer: {group.trainer.firstName} {group.trainer.lastName}
        </Text>

        {membership.note && (
          <Paper p="sm" bg="gray.0">
            <Text size="xs" fw={500} mb="xs">
              Your message:
            </Text>
            <Text size="xs" lineClamp={2}>
              {membership.note}
            </Text>
          </Paper>
        )}
      </Stack>
    </Paper>
  );
};

export const Groups = () => {
  const { data: groups, isLoading: groupsLoading } = client.useQuery(
    "get",
    "/owner/groups/",
  );
  const { data: requests, isLoading: requestsLoading } = client.useQuery(
    "get",
    "/owner/groups-requests/",
  );
  const [filter, setFilter] = useState("groups");

  if (groupsLoading || requestsLoading) {
    return <FancyLoader />;
  }

  const myGroups = groups?.groups || [];
  const myRequests = requests?.joinRequests || [];

  // Separate requests by status
  const pendingRequests = myRequests.filter(
    (req) => req.membership?.status === "REQUESTED",
  );
  const rejectedRequests = myRequests.filter(
    (req) => req.membership?.status === "REJECTED",
  );

  // Content for each tab
  const groupsContent =
    myGroups.length === 0 ? (
      <Paper p="xl" withBorder>
        <Stack align="center">
          <IconUsersGroup size={48} color="gray" />
          <Title order={4} ta="center">
            No groups yet.
          </Title>
          <Text c="dimmed" ta="center">
            You haven't joined any groups yet.
          </Text>
        </Stack>
      </Paper>
    ) : (
      <Stack gap="md">
        {myGroups.map((group) => (
          <GroupCard key={group.id} group={group} />
        ))}
      </Stack>
    );

  const pendingContent =
    pendingRequests.length === 0 ? (
      <Paper p="xl" withBorder>
        <Stack align="center">
          <IconUsersGroup size={48} color="gray" />
          <Title order={4} ta="center">
            No pending requests.
          </Title>
          <Text c="dimmed" ta="center">
            You haven't submitted any group join requests.
          </Text>
        </Stack>
      </Paper>
    ) : (
      <Stack gap="md">
        {pendingRequests.map((groupInfo) => (
          <RequestCard key={groupInfo.group.id} groupInfo={groupInfo} />
        ))}
      </Stack>
    );

  const rejectedContent =
    rejectedRequests.length === 0 ? (
      <Paper p="xl" withBorder>
        <Stack align="center">
          <IconUsersGroup size={48} color="gray" />
          <Title order={4} ta="center">
            No rejected requests.
          </Title>
          <Text c="dimmed" ta="center">
            You don't have any rejected join requests.
          </Text>
        </Stack>
      </Paper>
    ) : (
      <Stack gap="md">
        {rejectedRequests.map((groupInfo) => (
          <RequestCard key={groupInfo.group.id} groupInfo={groupInfo} />
        ))}
      </Stack>
    );

  return (
    <Stack>
      <Header title="My groups" backButton={true} />

      <Tabs value={filter} onChange={(value) => value && setFilter(value)}>
        <Tabs.List grow>
          <Tabs.Tab value="groups">My Groups ({myGroups.length})</Tabs.Tab>
          <Tabs.Tab value="pending">
            Pending ({pendingRequests.length})
          </Tabs.Tab>
          <Tabs.Tab value="rejected">
            Rejected ({rejectedRequests.length})
          </Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {filter === "groups" && groupsContent}
      {filter === "pending" && pendingContent}
      {filter === "rejected" && rejectedContent}
    </Stack>
  );
};

export const GroupDetail = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const { activeTab, onTabChange } = useHashTab<string>("info");
  const { navigate } = useNavigator();

  // First try to get the group as a member
  const { data: group, error } = client.useQuery(
    "get",
    "/owner/groups/{groupId}",
    {
      params: { path: { groupId: groupId } },
    },
  );

  const handleTrainerClick = () => {
    if (!group?.trainer.id) return;

    navigate({
      path: UniversalRoutes.TRAINERS.DETAIL,
      params: { trainerId: group.trainer.id },
    });
  };

  if (error) {
    const joinGroupPath = createPath({
      path: UniversalRoutes.GROUPS.JOIN,
      params: { groupId: groupId },
    });
    return <Redirect to={joinGroupPath} />;
  }

  // Show loading while both queries are pending
  if (!group) {
    return <FancyLoader />;
  }

  // If we have member data, show the member view
  return (
    <Stack>
      <Header title={group.name} backButton={true} />

      <Tabs value={activeTab} onChange={(value) => value && onTabChange(value)}>
        <Tabs.List grow>
          <Tabs.Tab value="info" leftSection={<IconInfoCircle size={16} />}>
            Info
          </Tabs.Tab>
          <Tabs.Tab value="files" leftSection={<IconFile size={16} />}>
            Files ({group.files.length})
          </Tabs.Tab>
          <Tabs.Tab value="sessions" leftSection={<IconCalendar size={16} />}>
            Sessions
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="info" pt="md">
          <Stack gap="md">
            <Paper
              p="md"
              withBorder
              style={{ cursor: "pointer" }}
              onClick={handleTrainerClick}
            >
              <Stack gap="sm">
                <Title order={5}>Trainer</Title>
                <Group gap="md">
                  <Portrait
                    profile={{
                      firstName: group.trainer.firstName,
                      lastName: group.trainer.lastName,
                      portraitFullPath: group.trainer.portraitFullPath,
                    }}
                    size="lg"
                    who="person"
                  />
                  <div>
                    <Text fw={500}>
                      {group.trainer.firstName} {group.trainer.lastName}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {group.trainer.locality}
                    </Text>
                  </div>
                </Group>
              </Stack>
            </Paper>

            <Paper p="md" withBorder>
              <Stack gap="sm">
                <Title order={5}>Description</Title>
                <Text>{group.description || "No description provided"}</Text>
              </Stack>
            </Paper>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="files" pt="md">
          <OwnerGroupFiles groupId={groupId} fileIds={group.files} />
        </Tabs.Panel>

        <Tabs.Panel value="sessions" pt="md">
          <Stack gap="md">
            {group.groupClassType && (
              <Paper p="md" withBorder>
                <Stack gap="xs">
                  <Text size="sm">
                    <strong>Class Type:</strong> {group.groupClassType.name}
                  </Text>
                  <Text size="sm">
                    <strong>Total Sessions:</strong> {group.sessions.length}
                  </Text>
                  {group.groupClassType.description && (
                    <Text size="sm" c="dimmed">
                      {group.groupClassType.description}
                    </Text>
                  )}
                  {group.groupClassType.preparation && (
                    <>
                      <Text size="sm" fw={500} mt="xs">
                        Preparation Notes:
                      </Text>
                      <Text size="sm" c="dimmed">
                        {group.groupClassType.preparation}
                      </Text>
                    </>
                  )}
                </Stack>
              </Paper>
            )}

            {group.sessions && group.sessions.length > 0 ? (
              <>
                <Title order={4}>Group Sessions</Title>
                {group.sessions.map((session) => (
                  <Paper key={session.id} p="md" withBorder>
                    <Group justify="space-between">
                      <div>
                        <Text fw={500}>
                          {group.groupClassType?.name ?? "Session"} {session.id}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {format(parseISO(session.scheduledAt), "PPP 'at' p")}
                        </Text>
                      </div>
                      <Badge
                        color={
                          session.status === "CANCELLED" ? "orange" : "green"
                        }
                      >
                        {session.status}
                      </Badge>
                    </Group>
                  </Paper>
                ))}
              </>
            ) : (
              <Paper p="md" withBorder>
                <Stack align="center" py="xl">
                  <IconCalendar size={48} color="gray" />
                  <Text c="dimmed" ta="center">
                    No sessions scheduled yet
                  </Text>
                </Stack>
              </Paper>
            )}
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
};

export const PublicGroupDetail = () => {
  const { groupId } = useParams<{ groupId: string }>();

  const {
    data: groupInfo,
    error,
    refetch: groupRefetch,
  } = client.useQuery("get", "/owner/groups/{groupId}/public", {
    params: { path: { groupId: groupId } },
  });

  const { data: membership, refetch: membershipRefetch } = client.useQuery(
    "get",
    "/owner/groups/{groupId}/membership",
    {
      params: { path: { groupId: groupId } },
    },
  );

  if (error) {
    <Stack>
      <Header title="Group" />
      <Paper p="xl" withBorder>
        <Stack align="center">
          <Text fw={500}>Group not found</Text>
          <Text c="dimmed" ta="center">
            This group doesn't exist or you don't have permission to view it.
          </Text>
        </Stack>
      </Paper>
    </Stack>;
  }

  if (!groupInfo) {
    return <FancyLoader />;
  }

  if (membership && membership.status === "APPROVED") {
    const groupDetailPath = createPath({
      path: UniversalRoutes.GROUPS.DETAIL,
      params: { groupId: groupId },
    });
    return <Redirect to={groupDetailPath} />;
  }

  const refetchBoth = async () => {
    await groupRefetch();
    await membershipRefetch();
  };

  return (
    <GroupJoinRequest
      group={groupInfo}
      membership={membership || undefined}
      groupRefetch={refetchBoth}
    />
  );
};
